import React, { useState, useEffect, createContext, useContext } from 'react';
import { auth } from '../../Services/firebase';
import { onAuthStateChanged } from "firebase/auth";
import { get, post } from '../../Services/api';
import Loading from '../Loading/Loading';

// Create a context to manage authentication state globally
const AuthContext = createContext();
// Custom hook to access authentication state
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {

    const [currentUser, setCurrentUser] = useState(null);
    const [userConfigExist, setUserConfigExist] = useState(null);
    const [userInfo, setUserInfo] = useState()
    const [loading, setLoading] = useState(true);
    const [has_token, setHasToken] = useState(null);
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    const [urlRedirect, setUrlRedirect] = useState();

    useEffect(() => {
        const { pathname, search } = window.location

        if (search)
            setUrlRedirect(pathname + search)

        if (isCreatingUser) return;

        // if user is not null check if they are fully configured
        if (currentUser) {
            handle_current_user()
        }

        const unsub = onAuthStateChanged(auth, async (user) => {
            try {
                if (user) {
                    setCurrentUser(user);
                    // handle new user
                    await handle_user(user);

                } else {
                    await logout()
                }
            } catch (e) {
                console.log(e.message)
            }
        });

        // Clean up the listener
        return unsub;
    }, [isCreatingUser]);

    async function logout() {
        setCurrentUser(null);
        setLoading(false);
    }

    async function getUserInfo(user) {
        const user_info = await get('/api/user/get-user', { email: user.email });
        setUserInfo(user_info)
    }

    async function create_new_user_db(user) {
        // if user exist in db then allow to login otherwise create the user in db and get the additional info needed from user
        const user_exist = await get('/api/public/user/user-exist',
            {
                email: user.email
            }
        );

        const [firstName, ...lastNameArray] = user.displayName.split(' ');
        const lastName = lastNameArray.join(' '); // Rejoin any remaining parts as the last name
        const params = {
            uid: user.uid,
            email: user.email,
            first_name: firstName,
            last_name: lastName,
            profilePic: user.photoURL.replace('s96-c', 's400-c'),
        };


        if (!user_exist) {
            await post('/api/user/create-user', params)
        }

    }

    async function user_config_exist(user) {

        try {
            const resp = await get('/api/user-config/user-config-exist',
                {
                    user_id: user.uid
                }
            );
            return resp
        } catch (e) {
            console.log(`Error in user_config_exist ${e.message}`)
        }
    }

    async function saveUserLogin(email) {
        try {
            await post('/api/user/update-user-last-login', { email: email })
        } catch (e) {
            console.log(`Error in saveUserLogin ${e.message}`)
        }
    }

    // Checks if the state changed user in onAuthStateChanged is a new user (Google authentication)
    async function handle_user(user) {
        const user_exist = await get('/api/public/user/user-exist',
            {
                email: user.email
            }
        );

        const currentUTCTimestamp = new Date().getTime();
        const create_date = Number(user.metadata.createdAt);

        // Create new db user if it has been less than 5 minutes the Google user has been created
        if (currentUTCTimestamp < create_date + 300000 && !user_exist) {
            await create_new_user_db(user)
        }


        // Check if user config exist
        const uc_exist = await user_config_exist(user)
        setUserConfigExist(uc_exist)

        // get user info
        await getUserInfo(user);
        // save user login info
        await saveUserLogin(user.email)
        setHasToken(true)
        setLoading(false);
    }

    // handle current user
    async function handle_current_user() {
        // Check if user config exist
        const uc_exist = await user_config_exist(currentUser)
        setUserConfigExist(uc_exist)
    }

    const values = {
        currentUser,
        userConfigExist,
        userInfo,
        has_token,
        setIsCreatingUser,

        urlRedirect,
        setUrlRedirect
    };

    return (
        <AuthContext.Provider value={values}>
            {
                !loading ? children : <Loading />
            }
        </AuthContext.Provider>
    );
};

export default AuthProvider;